import { Box, Checkbox, FormLabel } from "@mui/joy";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useCurrentVenueUuid, useVenueColorsQuery } from "../../hooks/venues";
import FormGroup from "./FormGroup";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

type Props = {
  value: string[];
  setValue: (colors: string[]) => void;
  options: string[];
  // When false, choosing a colour replaces the selection.
  multiple?: boolean;
};

export default function ColorsSelect({
  value: selectedColors,
  setValue,
  options,
  multiple = true,
}: Props) {
  return (
    <Box
      role="group"
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      {options.map((option) => (
        <Box
          key={option}
          sx={{
            backgroundColor: option,
            width: 32,
            height: 32,
            borderRadius: 24,
            border: "1px solid",
            borderColor: "var(--joy-palette-neutral-300)",
            position: "relative",
            "&:hover": {
              borderColor: "var(--joy-palette-neutral-400)",
            },
          }}
        >
          <Checkbox
            slotProps={{
              label: {
                sx: {
                  display: "none",
                },
              },
              action: ({ checked }) => ({
                sx: {
                  borderRadius: "24px",
                  borderWidth: "0!important",
                  background: "transparent!important",
                  outlineColor: checked ? "var(--joy-palette-neutral-900)" : "transparent",
                  outlineOffset: "1px",
                  outlineWidth: "2px",
                  outlineStyle: "solid",
                },
              }),
            }}
            disableIcon
            overlay
            label={option}
            value={option}
            checked={selectedColors.includes(option)}
            onChange={(_e) => {
              if (selectedColors.includes(option)) {
                setValue(multiple ? selectedColors.filter((color) => color !== option) : []);
              } else {
                setValue(multiple ? [...selectedColors, option] : [option]);
              }
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

export const ColorsFormInput = ({
  name,
  label,
  multiple = true,
}: {
  name: string;
  label: string;
  multiple?: boolean;
}) => {
  // const [field, meta, fieldHelper] = useField<string | null>(name);
  const { control } = useFormContext();
  const { field, formState, fieldState } = useController({
    name,
    control,
  });

  const initialValue = formState.defaultValues?.[name] || undefined;

  const { data: venueColors } = useVenueColorsQuery(useCurrentVenueUuid());

  const options = useMemo(() => {
    const base = venueColors?.map((color) => color.colors) || [];
    return [
      ...new Set([
        ...base,
        ...(initialValue?.split(":") || []),
        ...(field.value?.split(":") || []),
      ]),
    ];
  }, [initialValue, venueColors, field.value]);

  return (
    <FormGroup error={Boolean(fieldState.error)}>
      <FormLabel>{label}</FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      <ColorsSelect
        multiple={multiple}
        options={options}
        value={field.value?.split(":") || []}
        setValue={(colors) => field.onChange(colors.join(":") || null)}
      />
    </FormGroup>
  );
};
